import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/Store";
import {
    reduxDecreaseEstimateSelectedProductQuantity,
    reduxIncreaseEstimateSelectedProductQuantity,
    reduxSubtractEstimateSelectedProduct
} from "../../redux/EstimateSlice";
import {
    reduxDecreaseInstallSelectedProductQuantity,
    reduxIncreaseInstallSelectedProductQuantity,
    reduxSubtractInstallSelectedProduct,
    reduxDecreaseReservationSelectedProductQuantity,
    reduxIncreaseReservationSelectedProductQuantity,
    reduxSubtractReservationSelectedProduct,
    reduxChangeReservationIsDirectProduct
} from "../../redux/ReservationSlice";
import { SelectedProduct } from "../../model/product";
import { commaPrice } from "../../Validation";
import { serviceDivision, varHighStatus } from "../../Variable";
import arrow_below_ios from "../../assets/icons/arrow_below_ios.svg";
import closeIcon from "../../assets/icons/close.svg";
import shoppingBagIcon from "../../assets/icons/shopping_bag.svg";

interface Props {
    // step : 3 => 견적계산기, step : 5 => 서비스예약
    step: number;
    propsPriceList: number[];
    installPriceList?: number[];
    setDirectProductIndexList?: any;
    totalPrice?: number;
    isOpen?: boolean;
    setIsOpen?: (isOpen: boolean) => void;
}

const FloatingCart: React.FC<Props> = ({ 
    step, 
    propsPriceList,
    installPriceList = [],
    setDirectProductIndexList,
    totalPrice = 0,
    isOpen: externalIsOpen,
    setIsOpen: externalSetIsOpen,
}) => {
    const dispatch = useDispatch();
    const estimateData = useSelector((state: RootState) => state.estimateData);
    const reservationData = useSelector((state: RootState) => state.reservationData);
    
    const withRemoval = step === 5 && reservationData.reservation_division === 4 && reservationData.reservation_with_removal === 1;
    const [installSelectedProductList, setInstallSelectedProductList] = useState<SelectedProduct[]>([]);
    const [selectedProductList, setSelectedProductList] = useState<SelectedProduct[]>([]);
    const [priceList, setPriceList] = useState<string[]>([]);
    const [isDirectProduct, setIsDirectProduct] = useState<boolean>(false);
    const [internalIsOpen, setInternalIsOpen] = useState<boolean>(true);
    
    // 외부에서 제어하는 경우 externalIsOpen 사용, 그렇지 않으면 내부 상태 사용
    const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
    const setIsOpen = externalSetIsOpen || setInternalIsOpen;

    // Redux에서 선택된 제품 리스트 가져오기
    useEffect(() => {
        if (step === 3) {
            setSelectedProductList(estimateData.selected_product);
        } else if (step === 5) {
            setSelectedProductList(reservationData.selected_product);
        }
    }, [reservationData.selected_product, estimateData.selected_product, step]);

    useEffect(() => {
        if (step === 5) {
            setInstallSelectedProductList(reservationData.install_selected_product);
        } else {
            setInstallSelectedProductList([]);
        }
    }, [reservationData.install_selected_product, step]);

    // 가격 리스트 포맷팅
    useEffect(() => {
        let list: string[] = [];
        propsPriceList.forEach(price => {
            let str = commaPrice(price);
            list.push(str);
        });
        setPriceList(list);
    }, [propsPriceList]);

    // 직접입력 제품 상태 확인
    useEffect(() => {
        if (step === 5) {
            setIsDirectProduct(reservationData.is_direct_product);
        }
    }, [reservationData.is_direct_product, step]);

    // 카트 리스트에 담은 제품 빼기
    const subtractProduct = (idx: number) => {
        if (isDirectProduct && selectedProductList[idx].brand_ID === 99) {
            if (step === 5) {
                dispatch(reduxChangeReservationIsDirectProduct(false));
            }
        }

        if (step === 3) {
            dispatch(reduxSubtractEstimateSelectedProduct(idx));
        } else if (step === 5) {
            dispatch(reduxSubtractReservationSelectedProduct(idx));
        }
    };

    // 수량 감소
    const decreaseQuantity = (idx: number) => {
        if (step === 3) {
            dispatch(reduxDecreaseEstimateSelectedProductQuantity(idx));
        } else if (step === 5) {
            dispatch(reduxDecreaseReservationSelectedProductQuantity(idx));
        }
        
        if (isDirectProduct && selectedProductList[idx].brand_ID === 99 && setDirectProductIndexList) {
            let indexList = [...reservationData.direct_index_list];
            indexList.pop();
            setDirectProductIndexList(indexList);
        }
    };

    // 수량 증가
    const increaseQuantity = (idx: number) => {
        if (step === 3) {
            dispatch(reduxIncreaseEstimateSelectedProductQuantity(idx));
        } else if (step === 5) {
            dispatch(reduxIncreaseReservationSelectedProductQuantity(idx));
        }

        if (isDirectProduct && selectedProductList[idx].brand_ID === 99 && setDirectProductIndexList) {
            let indexList = [...reservationData.direct_index_list];
            indexList.push(reservationData.direct_index_list.length + 1);
            setDirectProductIndexList(indexList);
        }
    };

    const renderCartItem = (
        tmp: SelectedProduct,
        idx: number,
        priceText: string,
        handlers: {
            decreaseQuantity: (idx: number) => void;
            increaseQuantity: (idx: number) => void;
            subtractProduct: (idx: number) => void;
        }
    ) => (
        <div
            key={idx}
            className="flex justify-between items-center px-5 py-4 border-b border-gray-100 min-h-[80px] transition-colors duration-200 hover:bg-lightGray02 last:border-b-0"
            style={{ borderBottomStyle: 'solid' }}
        >
            <div className="flex flex-col items-start gap-2 flex-1 min-w-0 mr-4 overflow-hidden">
                <span className="text-[12px] font-medium text-mainBlack truncate w-full">{tmp.brand_kor_name}</span>
                <span className="text-mainBlack text-[15px] w-full">{tmp.product_name}</span>
            </div>
            <div className="flex items-center gap-3 text-middleGray02 flex-shrink-0">
                <div className="flex rounded-md border border-middleGray02 bg-mainWhite overflow-hidden">
                    {!withRemoval && ((step === 3 && estimateData.product_consumption_status !== 2) ||
                    (step === 5 && reservationData.product_consumption_status !== 2)) && (
                        <div
                            onClick={() => tmp.quantity > 1 && handlers.decreaseQuantity(idx)}
                            className="flex justify-center items-center h-8 min-w-[30px] px-2 bg-mainWhite cursor-pointer transition-colors duration-200 hover:bg-lightGray02 active:bg-middleGray01"
                        >
                            <button className="text-lg font-medium text-mainBlack bg-transparent cursor-pointer p-0 w-full h-full">-</button>
                        </div>
                    )}
                    <div className={`flex justify-center items-center h-8 min-w-[30px] px-2 text-mainBlack font-medium bg-lightGray02 ${
                        ((step === 3 && estimateData.product_consumption_status === 2) ||
                        (step === 5 && reservationData.product_consumption_status === 2) || withRemoval)
                        ? 'border border-middleGray02'
                        : 'border-l border-r border-middleGray02'
                    }`}>
                        <span>{withRemoval ? 1 : tmp.quantity}</span>
                    </div>
                    {!withRemoval && ((step === 3 && estimateData.product_consumption_status !== 2) ||
                    (step === 5 && reservationData.product_consumption_status !== 2)) && (
                        <div
                            onClick={() => handlers.increaseQuantity(idx)}
                            className="flex justify-center items-center h-8 min-w-[30px] px-2 bg-mainWhite cursor-pointer transition-colors duration-200 hover:bg-lightGray02 active:bg-middleGray01"
                        >
                            <button className="text-lg font-medium text-mainBlack bg-transparent cursor-pointer p-0 w-full h-full">+</button>
                        </div>
                    )}
                </div>
                <div className="flex justify-end items-center min-w-[100px] text-mainBlack font-medium">
                    <p className="m-0 text-[15px]">
                        {priceText ? priceText : 0}
                        <span className="text-[13px] font-normal text-middleGray02 ml-1">AP 포인트</span>
                    </p>
                </div>
                <button
                    onClick={() => handlers.subtractProduct(idx)}
                    className="w-8 h-8 p-0 border border-middleGray01 bg-mainWhite rounded-md cursor-pointer flex items-center justify-center transition-all duration-200 hover:bg-lightGray02 hover:border-middleGray02 active:scale-95"
                >
                    <img
                        src={closeIcon}
                        alt="제거"
                        className="w-3.5 h-3.5 brightness-0 saturate-100 opacity-60 hover:opacity-100"
                    />
                </button>
            </div>
        </div>
    );

    // 선택된 제품이 없으면 렌더링하지 않음
    if (withRemoval ? selectedProductList.length === 0 && installSelectedProductList.length === 0 : selectedProductList.length === 0) {
        return null;
    }

    const totalItems = selectedProductList.reduce((sum, item) => sum + item.quantity, 0)
        + (withRemoval ? installSelectedProductList.reduce((sum, item) => sum + item.quantity, 0) : 0);

    const getDivisionLabel = () => {
        if (step === 3) {
            const division = serviceDivision.find(d => d.value === String(estimateData.estimate_division));
            return division ? division.label : '';
        } else if (step === 5) {
            if (reservationData.reservation_division === 4 && reservationData.reservation_with_removal === 1) {
                return '탈거+시공';
            }
            const division = serviceDivision.find(d => d.value === String(reservationData.reservation_division));
            return division ? division.label : '';
        }
        return '';
    };

    const getProductConsumptionStatusLabel = () => {
        if (step === 5 && reservationData.reservation_division === 1) {
            switch (reservationData.product_consumption_status) {
                case 0: return '제품 소지';
                case 1: return '제품 미소지';
                case 2: return '출고요청';
                default: return '';
            }
        } else if (step === 3) {
            switch (estimateData.product_consumption_status) {
                case 0: return '제품 소지';
                case 1: return '제품 미소지';
                case 2: return '출고요청';
                default: return '';
            }
        }
        return '';
    };

    const getHighStatusLabel = () => {
        if (step === 3) {
            const highStatus = varHighStatus.find(h => h.value === String(estimateData.estimate_high_status));
            return highStatus ? highStatus.label : '';
        } else if (step === 5) {
            const highStatus = varHighStatus.find(h => h.value === String(reservationData.reservation_high_status));
            return highStatus ? highStatus.label : '';
        }
        return '';
    };

    const divisionLabel = getDivisionLabel();
    const productConsumptionStatusLabel = getProductConsumptionStatusLabel();
    const highStatusLabel = getHighStatusLabel();

    return (
        <div className="fixed bottom-5 right-5 z-[100] flex flex-col items-end gap-3 md:right-5 md:left-auto">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`relative w-14 h-14 rounded-full border-none cursor-pointer flex items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-300 z-[101] hover:scale-105 hover:shadow-[0_6px_16px_rgba(0,0,0,0.2)] active:scale-95 ${
                    isOpen ? 'bg-pointYellow hover:bg-pointYellow hover:brightness-95' : 'bg-mainBlack hover:bg-darkGray'
                }`}
            >
                <img
                    src={shoppingBagIcon}
                    alt="장바구니"
                    className={`w-6 h-6 ${isOpen ? 'brightness-0 saturate-100' : 'brightness-0 invert'}`}
                />
                {totalItems > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-pointYellow text-mainBlack rounded-[10px] text-xs font-semibold flex items-center justify-center border-2 border-mainWhite shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
                        {totalItems}
                    </span>
                )}
            </button>
            <div
                className={`relative w-full overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.12)] rounded-xl bg-mainWhite border border-middleGray01 flex flex-col transition-all duration-650 ease-in-out ${
                    isOpen
                        ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto max-h-[500px]'
                        : 'opacity-0 translate-y-2 scale-95 pointer-events-none max-h-0'
                }`}
            >
                <div className="flex flex-col bg-mainBlack border-b border-middleGray01">
                    <div className="flex justify-between items-center px-5 pt-4 pb-2">
                        <h3 className="m-0 text-lg font-semibold text-mainWhite">장바구니 ({totalItems})</h3>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="w-8 h-8 border-none bg-transparent cursor-pointer flex items-center justify-center rounded transition-colors duration-200 hover:bg-white/10"
                        >
                            <img
                                src={arrow_below_ios}
                                alt="열기"
                                className="w-[18px] h-[18px] brightness-0 invert"
                            />
                        </button>
                    </div>
                    {(divisionLabel || productConsumptionStatusLabel || highStatusLabel) && (
                        <div className="flex flex-wrap gap-2 px-5 pb-3">
                            {divisionLabel && (
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-pointYellow text-mainBlack">
                                    {divisionLabel}
                                </span>
                            )}
                            {productConsumptionStatusLabel && (
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {productConsumptionStatusLabel}
                                </span>
                            )}
                            {highStatusLabel && (
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {highStatusLabel}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className="bg-mainWhite overflow-y-auto flex-1">
                    {withRemoval && <div className="px-5 py-2 text-[13px] font-semibold text-middleGray02 bg-lightGray02">탈거할 제품 (탈거 비용 50%)</div>}
                    {selectedProductList.map((tmp, idx) => renderCartItem(tmp, idx, priceList[idx], {
                        decreaseQuantity,
                        increaseQuantity,
                        subtractProduct,
                    }))}
                    {withRemoval &&
                        <>
                            <div className="px-5 py-2 text-[13px] font-semibold text-middleGray02 bg-lightGray02">시공할 제품</div>
                            {installSelectedProductList.map((tmp, idx) => renderCartItem(tmp, idx, commaPrice(installPriceList[idx] ?? 0), {
                                decreaseQuantity: (index) => { dispatch(reduxDecreaseInstallSelectedProductQuantity(index)); },
                                increaseQuantity: (index) => { dispatch(reduxIncreaseInstallSelectedProductQuantity(index)); },
                                subtractProduct: (index) => { dispatch(reduxSubtractInstallSelectedProduct(index)); },
                            }))}
                        </>
                    }
                </div>
                <div className="flex justify-between items-center px-5 py-5 text-mainBlack text-lg font-semibold sticky bottom-0 bg-mainWhite border-t border-lightGray01" style={{ borderTopStyle: 'solid' }}>
                    <span>총 결제 금액</span>
                    <span>{commaPrice(totalPrice)} AP 포인트</span>
                </div>
            </div>
        </div>
    );
};

export default FloatingCart;
